import { useEffect } from "react";
import { Link } from "react-router-dom";
import { FileText, Shield, AlertTriangle, Scale, CreditCard, Brain, Database, Users, Building, Mail } from "lucide-react";

export default function TermsOfService() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const lastUpdated = "March 2, 2026";
  const effectiveDate = "March 2, 2026";

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-[#121212] text-white py-6 px-6 lg:px-12">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-[#BFFF00] rounded-xl flex items-center justify-center">
              <span className="text-[#121212] font-bold text-xl">F</span>
            </div>
            <span className="text-2xl font-bold font-['Manrope']">FinAI</span>
          </Link>
          <nav className="flex items-center gap-6 text-sm">
            <Link to="/privacy" className="hover:text-[#BFFF00]">Privacy</Link>
            <Link to="/security" className="hover:text-[#BFFF00]">Security</Link>
            <Link to="/login" className="bg-[#BFFF00] text-[#121212] px-4 py-2 rounded-full font-medium hover:bg-[#a8e600]">Login</Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <div className="bg-gradient-to-b from-[#F7FFE5] to-white py-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-16 h-16 bg-[#BFFF00] rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Scale className="w-8 h-8 text-[#121212]" />
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold text-[#121212] font-['Manrope'] mb-4">Terms of Service</h1>
          <p className="text-gray-600 text-lg">End User License Agreement (EULA)</p>
          <p className="text-gray-500 mt-2">Last Updated: {lastUpdated} | Effective: {effectiveDate}</p>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="prose prose-lg max-w-none">
          
          {/* Important Notice */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-8">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-amber-600 mt-1 flex-shrink-0" />
              <div>
                <h3 className="font-bold text-amber-900 mb-2">IMPORTANT LEGAL NOTICE</h3>
                <p className="text-amber-800 text-sm">
                  Please read these Terms of Service carefully before using FinAI by Revurge. By creating an account, accessing, or using our services, you agree to be bound by these terms and our Privacy Policy. If you do not agree to these terms, do not use our services.
                </p>
              </div>
            </div>
          </div>

          {/* Agreement */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#121212] mb-4 flex items-center gap-2">
              <FileText className="w-6 h-6 text-[#BFFF00]" />
              1. Agreement to Terms
            </h2>
            <p className="text-gray-700 leading-relaxed">
              This End User License Agreement ("Agreement" or "Terms") constitutes a legally binding contract between you ("User," "you," or "your") and Revurge Corporation ("Revurge," "Company," "we," "us," or "our"), a Delaware corporation with principal offices at 8 The Green, Dover, DE 19901.
            </p>
            <p className="text-gray-700 leading-relaxed mt-4">
              By clicking "I Accept," creating an account, or using FinAI services, you acknowledge that you have read, understood, and agree to be bound by these Terms, our <Link to="/privacy" className="text-blue-600 hover:underline">Privacy Policy</Link>, <Link to="/data-use" className="text-blue-600 hover:underline">Data Use Policy</Link>, and <Link to="/cookies" className="text-blue-600 hover:underline">Cookie Policy</Link>.
            </p>
          </section>

          {/* Data Collection Consent */}
          <section className="mb-12 bg-blue-50 rounded-2xl p-6">
            <h2 className="text-2xl font-bold text-[#121212] mb-4 flex items-center gap-2">
              <Database className="w-6 h-6 text-blue-600" />
              2. Consent for Data Collection and Use
            </h2>
            <p className="text-gray-700 mb-4">
              By using our services, you explicitly consent to the collection, processing, storage, and use of the following categories of data:
            </p>
            
            <div className="space-y-4">
              <div className="bg-white rounded-xl p-4 border border-blue-200">
                <h4 className="font-semibold text-[#121212] mb-2">Business Identification Data</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• <strong>Employer Identification Number (EIN)</strong> - for business verification and tax reporting</li>
                  <li>• Business name, address, and registration information</li>
                  <li>• Business structure and industry classification</li>
                </ul>
              </div>
              
              <div className="bg-white rounded-xl p-4 border border-blue-200">
                <h4 className="font-semibold text-[#121212] mb-2">Financial Account Data</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• <strong>Bank Account Information</strong> - account numbers, routing numbers, balances</li>
                  <li>• Transaction history and categorization data</li>
                  <li>• Payment card information (processed via PCI-compliant systems)</li>
                </ul>
              </div>
              
              <div className="bg-white rounded-xl p-4 border border-blue-200">
                <h4 className="font-semibold text-[#121212] mb-2">Personal Contact Information</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• <strong>Email Address</strong> - for account access and notifications</li>
                  <li>• <strong>Mobile Phone Number</strong> - for MFA and security alerts</li>
                  <li>• Physical address for service delivery and compliance</li>
                </ul>
              </div>
              
              <div className="bg-white rounded-xl p-4 border border-blue-200">
                <h4 className="font-semibold text-[#121212] mb-2">Sensitive Documents</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Tax returns, W-2s, 1099s, and other tax documents</li>
                  <li>• Financial statements and reports</li>
                  <li>• Invoices, contracts, and business agreements</li>
                </ul>
              </div>
            </div>
          </section>

          {/* AI Use Disclosure */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#121212] mb-4 flex items-center gap-2">
              <Brain className="w-6 h-6 text-[#BFFF00]" />
              3. Use of Artificial Intelligence
            </h2>
            <p className="text-gray-700 mb-4">
              FinAI utilizes artificial intelligence and machine learning technologies to provide enhanced services. By using our platform, you consent to:
            </p>
            
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li><strong>Document Analysis:</strong> AI-powered scanning and categorization of uploaded documents</li>
              <li><strong>Transaction Categorization:</strong> Automated classification of financial transactions</li>
              <li><strong>Anomaly Detection:</strong> AI-based identification of unusual patterns or potential errors</li>
              <li><strong>Predictive Insights:</strong> Machine learning-generated financial forecasts and recommendations</li>
              <li><strong>Natural Language Processing:</strong> AI-assisted document interpretation and data extraction</li>
            </ul>
            
            <div className="bg-gray-50 rounded-xl p-4 mt-4">
              <p className="text-sm text-gray-600">
                <strong>Important:</strong> AI-generated outputs are provided for informational purposes and should be reviewed by qualified professionals. We do not guarantee the accuracy of AI-generated content and recommend human verification for critical financial decisions.
              </p>
            </div>
          </section>

          {/* Data Retention */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#121212] mb-4 flex items-center gap-2">
              <Database className="w-6 h-6 text-[#BFFF00]" />
              4. Data Retention, Storage, and Sharing
            </h2>
            
            <h3 className="text-xl font-semibold text-[#121212] mt-6 mb-3">Data Retention</h3>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li><strong>Active Account Data:</strong> Retained throughout your active use of the service</li>
              <li><strong>Financial Records:</strong> Retained for 7 years per IRS requirements</li>
              <li><strong>Transaction Logs:</strong> Retained for 5 years for audit purposes</li>
              <li><strong>Deleted Accounts:</strong> Core data anonymized within 90 days; legal records retained as required</li>
            </ul>
            
            <h3 className="text-xl font-semibold text-[#121212] mt-6 mb-3">Data Storage</h3>
            <p className="text-gray-700">
              All data is stored in secure, SOC 2 Type II compliant data centers within the United States. Data is encrypted at rest using AES-256 encryption and in transit using TLS 1.3.
            </p>
            
            <h3 className="text-xl font-semibold text-[#121212] mt-6 mb-3">Data Sharing</h3>
            <p className="text-gray-700 mb-2">We share data only with:</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Authorized service providers under strict data processing agreements</li>
              <li>Financial institutions necessary for transaction processing</li>
              <li>Government authorities when legally required</li>
              <li>Your explicitly authorized third parties</li>
            </ul>
          </section>

          {/* Payment Terms */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#121212] mb-4 flex items-center gap-2">
              <CreditCard className="w-6 h-6 text-[#BFFF00]" />
              5. Payment Terms and Billing
            </h2>
            <p className="text-gray-700 mb-4">
              For paid subscription plans, you agree to the following terms:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li><strong>Subscription Fees:</strong> Billed monthly or annually as selected</li>
              <li><strong>Auto-Renewal:</strong> Subscriptions auto-renew unless cancelled before the renewal date</li>
              <li><strong>Payment Processing:</strong> Handled via Stripe, a PCI DSS Level 1 compliant payment processor</li>
              <li><strong>Platform Fees:</strong> Transaction fees may apply to payment links and invoices (see pricing page)</li>
              <li><strong>Refunds:</strong> Pro-rated refunds available within 30 days of purchase</li>
            </ul>
          </section>

          {/* Plaid Integration */}
          <section className="mb-12 bg-[#121212] text-white rounded-2xl p-6">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
              <Shield className="w-6 h-6 text-[#BFFF00]" />
              6. Bank Connection via Plaid
            </h2>
            <p className="text-gray-300 mb-4">
              Our bank connection feature is powered by Plaid Inc. By connecting your bank account, you:
            </p>
            <ul className="list-disc pl-6 text-gray-300 space-y-2">
              <li>Grant Plaid permission to access your bank account information</li>
              <li>Agree to Plaid's <a href="https://plaid.com/legal/end-user-privacy-policy/" target="_blank" rel="noopener noreferrer" className="text-[#BFFF00] hover:underline">End User Privacy Policy</a></li>
              <li>Authorize the secure transmission of your financial data to FinAI</li>
              <li>Understand that bank connection uses industry-standard OAuth authentication</li>
            </ul>
            <p className="text-gray-400 text-sm mt-4">
              Plaid is SOC 2 Type II certified and uses bank-level encryption to protect your credentials.
            </p>
          </section>

          {/* User Responsibilities */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#121212] mb-4 flex items-center gap-2">
              <Users className="w-6 h-6 text-[#BFFF00]" />
              7. User Responsibilities
            </h2>
            <p className="text-gray-700 mb-4">As a user of FinAI, you agree to:</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Provide accurate and complete information during registration</li>
              <li>Maintain the security of your login credentials</li>
              <li>Enable multi-factor authentication (MFA) for account security</li>
              <li>Notify us immediately of any unauthorized access</li>
              <li>Use the service only for lawful purposes</li>
              <li>Not attempt to circumvent security measures</li>
              <li>Comply with all applicable laws and regulations</li>
            </ul>
          </section>

          {/* Limitation of Liability */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#121212] mb-4">8. Limitation of Liability</h2>
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-gray-700 text-sm">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, REVURGE SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, USE, OR GOODWILL, ARISING OUT OF OR IN CONNECTION WITH YOUR USE OF THE SERVICE, WHETHER BASED ON WARRANTY, CONTRACT, TORT, OR ANY OTHER LEGAL THEORY.
              </p>
              <p className="text-gray-700 text-sm mt-4">
                OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID US IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM.
              </p>
            </div>
          </section>

          {/* Termination */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#121212] mb-4">9. Termination</h2>
            <p className="text-gray-700 mb-4">
              Either party may terminate this Agreement at any time. Upon termination:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Your access to the service will be disabled</li>
              <li>You may request export of your data within 30 days</li>
              <li>We will retain data as required by law and this Agreement</li>
              <li>All outstanding fees become immediately due</li>
            </ul>
          </section>

          {/* Governing Law */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#121212] mb-4">10. Governing Law and Disputes</h2>
            <p className="text-gray-700">
              This Agreement shall be governed by the laws of the State of Delaware, without regard to conflict of law principles. Any disputes shall be resolved through binding arbitration in accordance with the rules of the American Arbitration Association, with proceedings held in Delaware.
            </p>
          </section>

          {/* Contact */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#121212] mb-4 flex items-center gap-2">
              <Mail className="w-6 h-6 text-[#BFFF00]" />
              Contact Information
            </h2>
            <div className="bg-gray-50 rounded-xl p-6">
              <div className="flex items-start gap-4">
                <Building className="w-6 h-6 text-[#121212] mt-1" />
                <div>
                  <p className="font-semibold text-[#121212]">Revurge Corporation</p>
                  <p className="text-gray-600">8 The Green</p>
                  <p className="text-gray-600">Dover, DE 19901</p>
                  <p className="text-gray-600 mt-2">
                    <strong>General:</strong> <a href="mailto:info@revurge.com" className="text-blue-600 hover:underline">info@revurge.com</a>
                  </p>
                  <p className="text-gray-600">
                    <strong>Legal/Compliance:</strong> <a href="mailto:compliance@revurge.com" className="text-blue-600 hover:underline">compliance@revurge.com</a>
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Acceptance */}
          <section className="mb-12 bg-[#F7FFE5] border-2 border-[#BFFF00] rounded-2xl p-6">
            <h2 className="text-xl font-bold text-[#121212] mb-4">Acknowledgment and Acceptance</h2>
            <p className="text-gray-700">
              BY CREATING AN ACCOUNT OR USING FINAI SERVICES, YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTOOD, AND AGREE TO BE BOUND BY THESE TERMS OF SERVICE, INCLUDING THE CONSENT FOR COLLECTION AND USE OF YOUR DATA AS DESCRIBED HEREIN.
            </p>
          </section>

        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#121212] text-white py-12 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-400">
            <Link to="/privacy" className="hover:text-[#BFFF00]">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-[#BFFF00]">Terms of Service</Link>
            <Link to="/data-use" className="hover:text-[#BFFF00]">Data Use Policy</Link>
            <Link to="/cookies" className="hover:text-[#BFFF00]">Cookie Policy</Link>
            <Link to="/security" className="hover:text-[#BFFF00]">Security</Link>
            <Link to="/faq" className="hover:text-[#BFFF00]">FAQ</Link>
          </div>
          <p className="text-center text-gray-500 text-sm mt-6">
            © {new Date().getFullYear()} Revurge Corporation. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
